import {
  createResource, createSignal, createEffect, onMount,
  For, Show, batch,
} from 'solid-js';
import { createStore } from 'solid-js/store';
import { api } from '../lib/api.js';
import { currentUser } from '../lib/auth-store.js';
import { roleLabel } from '../lib/roles.js';
import { toast } from '../lib/toast.js';
import Modal from '../components/Modal.jsx';

// ── helpers ─────────────────────────────────────────────────────────────────

// Roles this caller may assign. A sub-admin can only create/assign user or
// readonly; admins get the full set (incl. subadmin). The backend enforces this
// too — this just shapes the dropdown.
function assignableRoles(callerRole) {
  // Only a Super Admin may create the backup-operator role (it's not in the
  // user/readonly delegated set a sub-admin manages).
  return callerRole === 'admin' ? ['readonly', 'user', 'backup', 'subadmin', 'admin'] : ['readonly', 'user'];
}

function roleBadgeClass(role) {
  return {
    admin: 'badge-role admin', subadmin: 'badge-role subadmin',
    user: 'badge-role user', readonly: 'badge-role readonly',
    backup: 'badge-role backup',
  }[role] ?? 'badge-role';
}

function fmtDate(unix) {
  return new Date(unix * 1000).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

// ── Tags chip input ──────────────────────────────────────────────────────────
// Free-text tags: type + Enter/comma to add, × or Backspace to remove.
function TagsInput(props) {
  const [draft, setDraft] = createSignal('');
  const add = (raw) => {
    const t = (raw ?? '').trim();
    setDraft('');
    if (!t || props.tags.includes(t)) return;
    props.setTags([...props.tags, t]);
  };
  const removeAt = (i) => props.setTags(props.tags.filter((_, idx) => idx !== i));
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft()); }
    else if (e.key === 'Backspace' && draft() === '' && props.tags.length) { removeAt(props.tags.length - 1); }
  };
  return (
    <div class="tags-input" classList={{ disabled: props.disabled }}>
      <For each={props.tags}>
        {(t, i) => (
          <span class="tag-chip">
            {t}
            <button type="button" class="tag-chip-x" disabled={props.disabled} onClick={() => removeAt(i())}>×</button>
          </span>
        )}
      </For>
      <input
        class="tags-input-field"
        value={draft()}
        disabled={props.disabled}
        placeholder={props.tags.length ? '' : (props.placeholder || 'Add tag…')}
        onInput={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => add(draft())}
      />
    </div>
  );
}

// ── UserForm modal ───────────────────────────────────────────────────────────

function UserForm(props) {
  const isCreate = () => !props.initial;
  // The role field is read-only when editing an account whose role the caller
  // can't assign (e.g. a sub-admin editing its own sub-admin account).
  const roleLocked = () => !isCreate() && !assignableRoles(currentUser()?.role).includes(props.initial.role);

  const [username, setUsername] = createSignal(props.initial?.username ?? '');
  const [password, setPassword] = createSignal('');
  const [role, setRole]         = createSignal(props.initial?.role ?? 'user');
  const [canCreateConn, setCanCreateConn] = createSignal(props.initial?.canCreateConnections ?? true);
  // Audit access (migration 043): off by default for new users; admin opts in.
  const [canViewAudit, setCanViewAudit] = createSignal(props.initial?.canViewOwnAudit ?? false);
  // Database-restore access for user / subadmin (migration 044). INERT for
  // readonly accounts — those use allowReadonlyRestore (045) as an explicit
  // override. Two parallel flags so the readonly path can't be granted by
  // accident. Off by default; admin opts in.
  const [canRestoreDb, setCanRestoreDb] = createSignal(props.initial?.canRestoreDatabase ?? false);
  const [allowROR, setAllowROR] = createSignal(props.initial?.allowReadonlyRestore ?? false);
  // Easy backup & restore (migration 046) — a UX simplification, not a gate.
  // Applies to any role; defaults off.
  const [easyBR, setEasyBR] = createSignal(props.initial?.easyBackupRestore ?? false);
  // Delete-backup grant (migration 047) — for backup operators using the
  // Backup/Restore tab. Admins always may delete; this opts in a specific operator.
  const [allowDelBak, setAllowDelBak] = createSignal(props.initial?.allowDeleteBackup ?? false);
  // Backup/Restore tab access (migration 058) — grants a non-admin/non-backup
  // account the Backup/Restore tab without changing their role. Off by default.
  const [canBackupTab, setCanBackupTab] = createSignal(props.initial?.canAccessBackupTab ?? false);
  // Ninocat Link / port forwarding (migration 073) — admin-only grant, off by default.
  const [allowPF, setAllowPF] = createSignal(props.initial?.allowPortForward ?? false);
  const [tags, setTags]         = createSignal(props.initial?.tags ?? []);
  const [busy, setBusy]         = createSignal(false);
  const [err, setErr]           = createSignal('');

  // Per-user form/import grants. Shown for runnable accounts (user/readonly),
  // and for an Admin (subadmin) target ONLY when the caller is a Super Admin —
  // the grant endpoints reject a sub-admin caller targeting a sub-admin
  // (subAdminMayTarget), so showing the section then would just 403 on save.
  // Super Admins (admin) need no grants (they can run everything). Loaded on
  // mount; saved alongside the user on submit.
  const showGrants = () => {
    if (isCreate()) return false;
    const t = props.initial.role;
    if (t === 'user' || t === 'readonly') return true;
    if (t === 'subadmin') return currentUser()?.role === 'admin';
    return false;
  };
  const [allForms, setAllForms]     = createSignal([]);
  const [allImports, setAllImports] = createSignal([]);
  const [formIds, setFormIds]       = createSignal(new Set());
  const [importIds, setImportIds]   = createSignal(new Set());
  const [grantsLoaded, setGrantsLoaded] = createSignal(false);
  const toggleIn = (get, set, id) => {
    const next = new Set(get());
    next.has(id) ? next.delete(id) : next.add(id);
    set(next);
  };
  onMount(async () => {
    if (!showGrants()) return;
    try {
      const [objs, fg, ig] = await Promise.all([
        api.get('/api/grantable-objects'),
        api.get(`/api/users/${props.initial.id}/dsl-form-grants`),
        api.get(`/api/users/${props.initial.id}/import-grants`),
      ]);
      setAllForms(objs.forms || []);
      setAllImports(objs.imports || []);
      setFormIds(new Set(fg.formIds || []));
      setImportIds(new Set(ig.importIds || []));
      setGrantsLoaded(true);
    } catch (e) {
      setErr(`Could not load grants: ${e.message}`);
    }
  });

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!username().trim()) { setErr('Username is required.'); return; }
    if (isCreate() && !password()) { setErr('Password is required.'); return; }

    setBusy(true);
    try {
      if (isCreate()) {
        const created = await api.post('/api/users', { username: username().trim(), password: password(), role: role(), canCreateConnections: canCreateConn(), canViewOwnAudit: canViewAudit(), canRestoreDatabase: canRestoreDb(), allowReadonlyRestore: allowROR(), easyBackupRestore: easyBR(), allowDeleteBackup: allowDelBak(), canAccessBackupTab: canBackupTab(), ...(currentUser()?.role === 'admin' ? { allowPortForward: allowPF() } : {}), tags: tags() });
        toast.success(`User "${username()}" created`);
        if (created?.warning) toast.warn(created.warning);
      } else {
        const body = { username: username().trim(), role: role(), canCreateConnections: canCreateConn(), canViewOwnAudit: canViewAudit(), canRestoreDatabase: canRestoreDb(), allowReadonlyRestore: allowROR(), easyBackupRestore: easyBR(), allowDeleteBackup: allowDelBak(), canAccessBackupTab: canBackupTab(), tags: tags() };
        if (currentUser()?.role === 'admin') body.allowPortForward = allowPF(); // admin-only grant
        if (password()) body.password = password();
        await api.patch(`/api/users/${props.initial.id}`, body);
        if (showGrants() && grantsLoaded()) {
          await api.put(`/api/users/${props.initial.id}/dsl-form-grants`, { formIds: [...formIds()] });
          await api.put(`/api/users/${props.initial.id}/import-grants`, { importIds: [...importIds()] });
        }
        toast.success(`User "${username()}" updated`);
      }
      props.onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={true} title={isCreate() ? 'New User' : `Edit — ${props.initial.username}`} onClose={props.onClose}>
      <form onSubmit={submit}>
        <div class="field">
          <label class="label">Username</label>
          <input
            class="input"
            type="text"
            autocomplete="off"
            value={username()}
            onInput={(e) => setUsername(e.target.value)}
            disabled={busy()}
          />
        </div>

        <div class="field">
          <label class="label">
            Password
            <Show when={!isCreate()}>
              <span class="muted" style="font-weight:400;margin-left:6px;">(leave blank to keep current)</span>
            </Show>
          </label>
          <input
            class="input"
            type="password"
            autocomplete="new-password"
            placeholder={isCreate() ? 'Required — min 8 characters' : 'New password…'}
            value={password()}
            onInput={(e) => setPassword(e.target.value)}
            disabled={busy()}
          />
        </div>

        <div class="field">
          <label class="label">Role</label>
          {/* Locked when editing an account whose role this caller can't assign
              (e.g. a sub-admin editing its own account) — shown read-only. */}
          <Show
            when={!roleLocked()}
            fallback={<input class="input" value={roleLabel(role())} disabled />}
          >
            <select
              class="select"
              value={role()}
              onChange={(e) => setRole(e.target.value)}
              disabled={busy()}
            >
              <For each={assignableRoles(currentUser()?.role)}>{(r) => <option value={r}>{roleLabel(r)}</option>}</For>
            </select>
          </Show>
          {/* Role-hint text uses the new display labels — Admin (was "Sub-admin")
              for the delegated user-manager role; Super Admin (was "Admin") for
              the full-power account. Role IDs unchanged. See lib/roles.js. */}
          <div class="role-hint">
            <Show when={role() === 'readonly'}>Read-only: access to specific connections/databases configured by a Super Admin.</Show>
            <Show when={role() === 'user'}>User: can open MySQL connections, run queries, and manage their own connections.</Show>
            <Show when={role() === 'subadmin'}>Admin: can manage User and Read-only accounts and grant forms/imports — but cannot see or manage Super Admins.</Show>
            <Show when={role() === 'admin'}>Super Admin: full access including user management and shared connections.</Show>
          </div>
        </div>

        {/* The can-create-connections flag is admin-controlled; hidden for a
            sub-admin (the backend keeps the default on create / preserves on edit). */}
        <Show when={currentUser()?.role === 'admin'}>
        <div class="field">
          <label class="label">Connections</label>
          <Show
            when={role() === 'user' || role() === 'subadmin'}
            fallback={
              <div class="role-hint">
                <Show when={role() === 'admin'}>Admins can always create connections.</Show>
                <Show when={role() === 'readonly'}>Read-only users cannot create connections.</Show>
              </div>
            }
          >
            <label class="checkbox-label">
              <input
                type="checkbox"
                checked={canCreateConn()}
                onChange={(e) => setCanCreateConn(e.target.checked)}
                disabled={busy()}
              />
              Allow creating connections
            </label>
            <div class="role-hint">
              When off, this user can still open connections shared with them, but the
              “+ New Connection” button is hidden. With no shared connections either,
              the Connections tab is hidden entirely (only Forms remains).
            </div>
          </Show>
        </div>

        {/* Audit-access flag (admin-controlled, applies to every non-admin
            role). When ON, the user sees the Audit tab and /api/history is
            force-scoped to their own activity server-side; admins are never
            affected. Hidden for the admin role since they always see all. */}
        <Show when={role() !== 'admin' && role() !== 'readonly'}>
          <div class="field">
            <label class="label">Audit</label>
            <label class="checkbox-label">
              <input
                type="checkbox"
                checked={canViewAudit()}
                onChange={(e) => setCanViewAudit(e.target.checked)}
                disabled={busy()}
              />
              Allow viewing their own audit log
            </label>
            <div class="role-hint">
              When off, the Audit tab is hidden. When on, a <strong>User</strong> sees ONLY
              their own query/write activity. An <strong>Admin</strong> additionally sees what
              User/Read-only accounts did on the connections shared to the Admin's tags (their
              team). The by-user filter and prune control stay hidden, and the server enforces
              the scope (a tampered request can't widen it). Super Admins always see the full
              fleet audit regardless. (Read-only accounts are locked to Forms/Imports and have
              no Audit tab.)
            </div>
          </div>

          {/* Database-restore flag for user / subadmin roles (migration 044).
              Hidden for readonly — that role uses allowReadonlyRestore (045)
              as a deliberately separate override so the no-writes guarantee
              can never be widened by accident. Hidden for admin since they
              always have full restore access regardless. */}
          <Show when={role() === 'user' || role() === 'subadmin'}>
            <div class="field">
              <label class="label">Database restore</label>
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={canRestoreDb()}
                  onChange={(e) => setCanRestoreDb(e.target.checked)}
                  disabled={busy()}
                />
                Allow restoring a database from a .sql dump
              </label>
              <div class="role-hint">
                When off, the "Restore database…" menu items are hidden and the restore
                endpoints return 403. When on, the user can restore on any connection +
                database they already have access to — the normal access checks still
                apply, this flag only widens the "who may attempt restore at all" gate.
                <strong> ⚠ Highly destructive:</strong> a restore executes arbitrary SQL
                from the uploaded dump (DDL + DML + DEFINER routines). Grant only to
                users you trust with full database control. The Delete-database action
                stays admin-only regardless of this flag.
              </div>
            </div>
          </Show>

          {/* Readonly restore override (migration 045). The DEFAULT for a
              readonly account is "cannot restore" — restore is a write and
              would silently widen the no-writes guarantee. This override is
              the explicit, per-user opt-in that says "yes, this SPECIFIC
              readonly operator (data-recovery, staging refresh) needs it".
              Deliberately a separate flag from canRestoreDatabase so the
              readonly path is never granted by accident. */}
          <Show when={role() === 'readonly'}>
            <div class="field">
              <label class="label">Database restore (readonly override)</label>
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={allowROR()}
                  onChange={(e) => setAllowROR(e.target.checked)}
                  disabled={busy()}
                />
                Allow this Read-only user to restore a database
              </label>
              <div class="role-hint">
                Default OFF — the Read-only role normally cannot restore (restore is a
                write operation). Tick this only for the specific operator who needs
                it (e.g. someone refreshing a staging database from a backup, or a
                data-recovery role). Their normal queries stay read-only; only the
                restore upload path is opened.
                <div style="margin-top:6px;color:var(--danger,#ef4444);">
                  <strong>⚠ This widens the Read-only role's no-writes guarantee for
                  this one user.</strong> A restore upload can rewrite whole databases
                  on connections they have access to. The Delete-database action stays
                  admin-only regardless.
                </div>
              </div>
            </div>
          </Show>
        </Show>
        </Show>

        {/* Ninocat Link / port forwarding (migration 073) — admin-only grant;
            admins always have it via their role. */}
        <Show when={currentUser()?.role === 'admin' && role() !== 'admin'}>
          <div class="field">
            <label class="label">Port forwarding</label>
            <label class="checkbox-label">
              <input type="checkbox" checked={allowPF()} onChange={(e) => setAllowPF(e.target.checked)} disabled={busy()} />
              Allow Ninocat Link (local ports to their connections)
            </label>
            <div class="role-hint">
              Lets this user download Ninocat Link and reach the connections they can use from
              apps on their PC (127.0.0.1:port). Apps log in with their own MySQL account and talk
              to MySQL directly — so Ninocat's read-only role, database grants and query audit do
              not apply to that traffic. Turning this off cuts their open Link sessions.
            </div>
          </div>
        </Show>

        {/* Backup/Restore tab access (migration 058). Admin + the backup role
            already have the tab via their role, so this grant is only shown for
            the other roles — it opens the tab for a specific user without making
            them a backup operator. */}
        <Show when={role() !== 'admin' && role() !== 'backup'}>
          <div class="field">
            <label class="label">Backup / Restore tab</label>
            <label class="checkbox-label">
              <input
                type="checkbox"
                checked={canBackupTab()}
                onChange={(e) => setCanBackupTab(e.target.checked)}
                disabled={busy()}
              />
              Allow access to the Backup / Restore tab
            </label>
            <div class="role-hint">
              When on, this user sees the Backup / Restore tab in the ☰ Open menu and can
              take + restore server-side backups on the connections / databases they
              already have access to. The normal per-connection / per-database access
              checks still apply, and deleting a stored backup remains a separate grant
              (“Allow deleting stored backups”, for the backup role). Admins and the
              backup role have this tab via their role regardless of this flag.
            </div>
          </div>
        </Show>

        {/* Easy backup & restore (migration 046) — a UX simplification, not a
            capability gate, so it applies to EVERY role (including admin) and
            sits outside the non-admin block above. When on, this user always
            gets the simplified backup/restore dialogs; everyone else can opt in
            ad-hoc by holding Shift when opening those menus. */}
        <div class="field">
          <label class="label">Backup &amp; restore UI</label>
          <label class="checkbox-label">
            <input
              type="checkbox"
              checked={easyBR()}
              onChange={(e) => setEasyBR(e.target.checked)}
              disabled={busy()}
            />
            Use easy backup &amp; restore (simplified UI)
          </label>
          <div class="role-hint">
            When on, the backup dialog hides its option checkboxes (everything is
            included by default, leaving just a note field) and restore drops to a
            minimal flow — existing-database only, locked to the database the user
            right-clicked, no type-to-confirm, with the restore-timestamp marker
            always added. It does <strong>not</strong> change what the user is
            allowed to do (restore still needs the restore flag above); it only
            simplifies the UI. Anyone can get the same simplified flow on demand by
            holding <span class="mono">Shift</span> when opening a backup/restore menu.
          </div>
          {/* Delete-backup grant — relevant to the Backup/Restore tab (backup
              operators). Admins always may delete, so it's only shown for the
              backup role. */}
          <Show when={role() === 'backup'}>
            <label class="checkbox-label" style="margin-top:10px;">
              <input
                type="checkbox"
                checked={allowDelBak()}
                onChange={(e) => setAllowDelBak(e.target.checked)}
                disabled={busy()}
              />
              Allow deleting stored backups
            </label>
            <div class="role-hint">
              When off, this operator can take + restore backups but can't delete them
              from the Backup/Restore tab. Deleting a backup destroys a recovery point,
              so it's the extra trust tier — grant it only to operators you trust to
              prune the backup store.
            </div>
          </Show>
        </div>

        {/* Backup-operator role explainer — its whole app is the Backup/Restore tab. */}
        <Show when={role() === 'backup'}>
          <div class="role-hint" style="margin-top:-6px;">
            <strong>Backup Operator</strong>: this account sees only the Backup / Restore
            tab — no connections, objects, or query tabs. Grant it access to specific
            connections and databases with the <strong>Access</strong> button on the user
            row (same granular per-connection / per-database whitelist as a Read-only user);
            it can back up + restore exactly those. Note this is a workflow boundary, not a
            data-confidentiality one — an operator who can dump a database can read all of
            its data via the dump.
          </div>
        </Show>

        <div class="field">
          <label class="label">Tags <span class="muted" style="font-weight:400;">(teams — used to scope shared connections)</span></label>
          <TagsInput tags={tags()} setTags={setTags} disabled={busy()} placeholder="Type a tag, press Enter…" />
        </div>

        <Show when={showGrants()}>
          <div class="field">
            <label class="label">Forms &amp; imports access <span class="muted" style="font-weight:400;">(what this user may run)</span></label>
            <Show when={grantsLoaded()} fallback={<div class="muted" style="font-size:12px;">Loading…</div>}>
              <div class="grant-objects">
                <div class="grant-objects-group">
                  <div class="grant-objects-title">Forms</div>
                  <Show when={allForms().length} fallback={<div class="muted" style="font-size:12px;">No forms defined.</div>}>
                    <For each={allForms()}>
                      {(f) => (
                        <label class="checkbox-label">
                          <input type="checkbox" checked={formIds().has(f.id)} disabled={busy()}
                                 onChange={() => toggleIn(formIds, setFormIds, f.id)} />
                          {f.name}
                        </label>
                      )}
                    </For>
                  </Show>
                </div>
                <div class="grant-objects-group">
                  <div class="grant-objects-title">Imports</div>
                  <Show when={allImports().length} fallback={<div class="muted" style="font-size:12px;">No imports defined.</div>}>
                    <For each={allImports()}>
                      {(im) => (
                        <label class="checkbox-label">
                          <input type="checkbox" checked={importIds().has(im.id)} disabled={busy()}
                                 onChange={() => toggleIn(importIds, setImportIds, im.id)} />
                          {im.name}
                        </label>
                      )}
                    </For>
                  </Show>
                </div>
              </div>
            </Show>
          </div>
        </Show>

        <Show when={err()}>
          <div class="error-text">{err()}</div>
        </Show>

        <div class="modal-actions">
          <button type="button" class="btn btn-ghost" onClick={props.onClose} disabled={busy()}>Cancel</button>
          <button type="submit" class="btn" disabled={busy()}>
            {busy() ? 'Saving…' : isCreate() ? 'Create User' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Grant modal ──────────────────────────────────────────────────────────────
//
// Shows all connections as checkboxes. Expanding a checked connection reveals
// database checkboxes (loaded on-demand from the API).
//
// Grant shape: { connectionId, databases: string[] }
// databases=[] means all databases in that connection are allowed.

function GrantModal(props) {
  // props.user      — the readonly user we're editing
  // props.onClose   — close callback
  // props.onSaved   — saved callback

  // Load all connections (admin sees all).
  const [allConns] = createResource(async () => {
    const { connections } = await api.get('/api/connections');
    return connections ?? [];
  });

  // Load existing grants for this user.
  const [existingGrants] = createResource(async () => {
    const { grants } = await api.get(`/api/users/${props.user.id}/grants`);
    return grants ?? [];
  });

  // Local editing state keyed by connection id.
  // Shape per entry: { checked, allDbs, selectedDbs: Set<string>, dbList: string[]|null, dbLoading, dbError }
  const [grantState, setGrantState] = createStore({});
  const [initialized, setInitialized] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [err, setErr] = createSignal('');

  // Once both resources load, initialise the local state.
  createEffect(() => {
    const conns = allConns();
    const grants = existingGrants();
    if (!conns || !grants || initialized()) return;

    // Build a lookup from existing grants.
    const grantMap = {};
    for (const g of grants) {
      grantMap[g.connectionId] = new Set(g.databases ?? []);
    }

    const initial = {};
    for (const c of conns) {
      const dbSet = grantMap[c.id];
      initial[c.id] = {
        checked:    !!dbSet,
        allDbs:     !!dbSet && dbSet.size === 0,
        selectedDbs: dbSet ?? new Set(),
        dbList:     null,   // will be loaded on expand
        dbLoading:  false,
        dbError:    '',
      };
    }
    setGrantState(initial);
    setInitialized(true);
  });

  // Toggle a connection grant on/off.
  const toggleConn = (connId) => {
    const current = grantState[connId];
    if (!current) return;
    setGrantState(connId, 'checked', !current.checked);
    if (!current.checked) {
      // Just checked — load databases, default to all allowed.
      setGrantState(connId, 'allDbs', true);
      loadDatabases(connId);
    }
  };

  // Load the database list for a connection.
  const loadDatabases = async (connId) => {
    if (grantState[connId]?.dbList) return; // already loaded
    setGrantState(connId, 'dbLoading', true);
    setGrantState(connId, 'dbError', '');
    try {
      const { databases } = await api.get(`/api/connections/${connId}/databases`);
      setGrantState(connId, 'dbList', (databases ?? []).map(d => d.name));
    } catch (e) {
      setGrantState(connId, 'dbError', e.message);
    } finally {
      setGrantState(connId, 'dbLoading', false);
    }
  };

  // Toggle "all databases" checkbox.
  const toggleAllDbs = (connId) => {
    setGrantState(connId, 'allDbs', !grantState[connId].allDbs);
    if (!grantState[connId].allDbs) {
      // Just switched to "all" — clear individual selections.
      setGrantState(connId, 'selectedDbs', new Set());
    }
  };

  // Toggle an individual database checkbox.
  const toggleDb = (connId, dbName) => {
    const cur = new Set(grantState[connId].selectedDbs);
    if (cur.has(dbName)) cur.delete(dbName);
    else cur.add(dbName);
    batch(() => {
      setGrantState(connId, 'selectedDbs', cur);
      setGrantState(connId, 'allDbs', false);
    });
  };

  // Expand (show db list) for a checked connection.
  const expandConn = (connId) => {
    if (!grantState[connId]?.checked) return;
    loadDatabases(connId);
  };

  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      const grants = [];
      const noDbs = []; // checked connections with no database picked
      for (const [connIdStr, state] of Object.entries(grantState)) {
        if (!state.checked) continue;
        const connId = Number(connIdStr);
        // The server reads `databases: []` as ALL databases, so a connection
        // with "All databases" off and nothing ticked must never be sent as []
        // — unticking the last database would grant everything.
        if (!state.allDbs && state.selectedDbs.size === 0) { noDbs.push(connId); continue; }
        const databases = state.allDbs ? [] : [...state.selectedDbs];
        grants.push({ connectionId: connId, databases });
      }
      if (noDbs.length) {
        const names = noDbs.map((id) => allConns()?.find((c) => c.id === id)?.name ?? `#${id}`);
        setErr(`No database selected for ${names.join(', ')}. Tick "All databases" or at least one database, or untick the connection to remove access.`);
        return;
      }
      await api.put(`/api/users/${props.user.id}/grants`, { grants });
      toast.success(`Access grants saved for ${props.user.username}`);
      props.onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={true} title={`Access — ${props.user.username}`} onClose={props.onClose}>
      <p class="muted" style="margin-bottom:14px;font-size:12.5px;">
        Select which connections this user can open, and optionally restrict access to specific databases within each connection.
      </p>

      <Show when={!allConns.loading && initialized()} fallback={<div class="muted">Loading…</div>}>
        <Show
          when={allConns()?.length > 0}
          fallback={<div class="muted">No connections exist yet.</div>}
        >
          <div class="grant-list">
            <For each={allConns()}>
              {(conn) => {
                const state = () => grantState[conn.id];
                return (
                  <div class="grant-conn" classList={{ checked: state()?.checked }}>
                    {/* Connection row */}
                    <label class="grant-conn-header">
                      <input
                        type="checkbox"
                        checked={state()?.checked}
                        onChange={() => toggleConn(conn.id)}
                      />
                      <span class="grant-conn-name">{conn.name}</span>
                      <span class="grant-conn-meta mono muted">{conn.host}:{conn.port}</span>
                    </label>

                    {/* Database selector — only shown when connection is checked */}
                    <Show when={state()?.checked}>
                      <div class="grant-db-section" onClick={() => expandConn(conn.id)}>
                        <Show when={state()?.dbLoading}>
                          <div class="muted" style="padding:6px 0;font-size:12px;">Loading databases…</div>
                        </Show>
                        <Show when={state()?.dbError}>
                          <div class="error-text" style="font-size:12px;">{state()?.dbError}</div>
                        </Show>
                        <Show when={!state()?.dbLoading && !state()?.dbError}>
                          {/* "All databases" option */}
                          <label class="grant-db-row grant-db-all">
                            <input
                              type="checkbox"
                              checked={state()?.allDbs}
                              onChange={() => toggleAllDbs(conn.id)}
                            />
                            <span>All databases</span>
                          </label>

                          {/* Individual database checkboxes */}
                          <Show when={state()?.dbList?.length > 0}>
                            <div class="grant-db-divider">or select specific:</div>
                            <For each={state()?.dbList}>
                              {(dbName) => (
                                <label class="grant-db-row">
                                  <input
                                    type="checkbox"
                                    checked={state()?.selectedDbs?.has(dbName)}
                                    onChange={() => toggleDb(conn.id, dbName)}
                                    disabled={state()?.allDbs}
                                  />
                                  <span class="mono">{dbName}</span>
                                </label>
                              )}
                            </For>
                          </Show>
                          <Show when={state()?.dbList !== null && state()?.dbList?.length === 0}>
                            <div class="muted" style="font-size:12px;padding:4px 0;">No databases found.</div>
                          </Show>
                          <Show when={state()?.dbList === null}>
                            <button
                              type="button"
                              class="btn btn-ghost btn-sm"
                              style="margin-top:6px;"
                              onClick={() => loadDatabases(conn.id)}
                            >
                              Load databases
                            </button>
                          </Show>
                        </Show>
                      </div>
                    </Show>
                  </div>
                );
              }}
            </For>
          </div>
        </Show>
      </Show>

      <Show when={err()}>
        <div class="error-text" style="margin-top:10px;">{err()}</div>
      </Show>

      <div class="modal-actions">
        <button class="btn btn-ghost" onClick={props.onClose} disabled={busy()}>Cancel</button>
        <button class="btn" onClick={save} disabled={busy() || !initialized()}>
          {busy() ? 'Saving…' : 'Save Access'}
        </button>
      </div>
    </Modal>
  );
}

// ── Users page ───────────────────────────────────────────────────────────────

export default function Users() {
  const [users, { refetch }] = createResource(async () => {
    const { users } = await api.get('/api/users');
    return users ?? [];
  });

  const [editing, setEditing]   = createSignal(null); // null | 'new' | User
  const [deleting, setDeleting] = createSignal(null); // User being deleted
  const [granting, setGranting] = createSignal(null); // readonly User for access modal

  const onSaved = () => { setEditing(null); refetch(); };

  const confirmDelete = async () => {
    const u = deleting();
    if (!u) return;
    try {
      await api.delete(`/api/users/${u.id}`);
      toast.success(`Deleted user "${u.username}"`);
      setDeleting(null);
      refetch();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const me = () => currentUser();

  return (
    <div class="page">
      <header class="page-header">
        <div>
          <h1>User Management</h1>
          <p class="muted">Manage who can access Ninocat and what they can do.</p>
        </div>
        <button class="btn" onClick={() => setEditing('new')}>+ New User</button>
      </header>

      <Show when={users()} fallback={<div class="muted">Loading…</div>}>
        <div class="data-table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Tags</th>
                <th>Joined</th>
                <th style="width:200px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <For each={users()}>
                {(u) => (
                  <tr classList={{ 'row-me': u.id === me()?.id }}>
                    <td>
                      <span class="user-name">{u.username}</span>
                      <Show when={u.id === me()?.id}>
                        <span class="you-tag">you</span>
                      </Show>
                    </td>
                    <td>
                      <span class={roleBadgeClass(u.role)}>{roleLabel(u.role)}</span>
                    </td>
                    <td>
                      <Show when={(u.tags ?? []).length > 0} fallback={<span class="muted">—</span>}>
                        <div class="tag-chip-row">
                          <For each={u.tags}>{(t) => <span class="tag-chip tag-chip-static">{t}</span>}</For>
                        </div>
                      </Show>
                    </td>
                    <td class="muted mono" style="font-size:12px;">{fmtDate(u.createdAt)}</td>
                    <td>
                      <div class="row-actions">
                        <Show when={(u.role === 'readonly' || u.role === 'backup') && me()?.role === 'admin'}>
                          <button
                            class="btn btn-ghost btn-sm"
                            title="Configure which connections and databases this user can access"
                            onClick={() => setGranting(u)}
                          >
                            Access
                          </button>
                        </Show>
                        <button class="btn btn-ghost btn-sm" onClick={() => setEditing(u)}>Edit</button>
                        <button
                          class="btn btn-ghost btn-sm btn-danger"
                          disabled={u.id === me()?.id}
                          title={u.id === me()?.id ? 'Cannot delete your own account' : ''}
                          onClick={() => setDeleting(u)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>

      {/* Create / Edit modal */}
      <Show when={editing()}>
        <UserForm
          initial={editing() === 'new' ? null : editing()}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      </Show>

      {/* Access grants modal — readonly users only */}
      <Show when={granting()}>
        <GrantModal
          user={granting()}
          onClose={() => setGranting(null)}
          onSaved={() => setGranting(null)}
        />
      </Show>

      {/* Delete confirmation modal */}
      <Show when={deleting()}>
        <Modal open={true} title="Delete User" onClose={() => setDeleting(null)}>
          <p>
            Are you sure you want to delete <strong>{deleting()?.username}</strong>?
            This will immediately invalidate all their sessions.
          </p>
          <div class="modal-actions">
            <button class="btn btn-ghost" onClick={() => setDeleting(null)}>Cancel</button>
            <button class="btn btn-danger-solid" onClick={confirmDelete}>Delete User</button>
          </div>
        </Modal>
      </Show>
    </div>
  );
}
